import { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { Profile } from '@/lib/index';
import type { User, Session } from '@supabase/supabase-js';

interface AuthContextValue {
  user: User | null;
  profile: Profile | null;
  loading: boolean;
  isSuperAdmin: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

async function fetchProfile(userId: string): Promise<Profile | null> {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('id, email, full_name, role, created_at, updated_at')
      .eq('id', userId)
      .maybeSingle();
    if (error) {
      console.warn('fetchProfile error:', error.message);
      return null;
    }
    return data as Profile | null;
  } catch (e) {
    console.warn('fetchProfile exception:', e);
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const initializedRef = useRef(false);
  // Timeout di sicurezza: se dopo 5 secondi siamo ancora in loading, forza il completamento
  const safetyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isSuperAdmin =
    profile?.role === 'superadmin' ||
    user?.email === 'alessandro1171@gmail.com';

  useEffect(() => {
    let cancelled = false;

    // Safety timeout: dopo 5s imposta loading=false comunque
    safetyTimeoutRef.current = setTimeout(() => {
      if (!cancelled && !initializedRef.current) {
        console.warn('Auth init timeout — forcing loading=false');
        setLoading(false);
        initializedRef.current = true;
      }
    }, 5000);

    // Prima, ascolta i cambi di stato auth (viene chiamato subito con la sessione corrente)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event: string, session: Session | null) => {
        if (cancelled) return;

        console.log('Auth state change:', event, session?.user?.email || 'no user');

        if (session?.user) {
          setUser(session.user);
          // Carica il profilo in background
          fetchProfile(session.user.id).then((p) => {
            if (!cancelled) setProfile(p);
          });
        } else {
          setUser(null);
          setProfile(null);
        }

        // Completa loading al primo evento
        if (!initializedRef.current) {
          initializedRef.current = true;
          if (safetyTimeoutRef.current) {
            clearTimeout(safetyTimeoutRef.current);
          }
          setLoading(false);
        } else {
          setLoading(false);
        }
      }
    );

    // Poi verifica manualmente la sessione per sicurezza (fallback)
    const checkSession = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (cancelled) return;

        // Se onAuthStateChange non è ancora stato chiamato, inizializza manualmente
        if (!initializedRef.current) {
          initializedRef.current = true;
          if (safetyTimeoutRef.current) {
            clearTimeout(safetyTimeoutRef.current);
          }
          if (session?.user) {
            setUser(session.user);
            const p = await fetchProfile(session.user.id);
            if (!cancelled) setProfile(p);
          } else {
            setUser(null);
            setProfile(null);
          }
          if (!cancelled) setLoading(false);
        }
      } catch (e) {
        console.error('checkSession error:', e);
        if (!cancelled && !initializedRef.current) {
          initializedRef.current = true;
          setLoading(false);
        }
      }
    };

    // Esegui checkSession dopo un breve ritardo per dare priorità a onAuthStateChange
    const sessionCheckTimeout = setTimeout(checkSession, 1000);

    return () => {
      cancelled = true;
      subscription.unsubscribe();
      clearTimeout(sessionCheckTimeout);
      if (safetyTimeoutRef.current) {
        clearTimeout(safetyTimeoutRef.current);
      }
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    try {
      // Non impostiamo loading=true qui: onAuthStateChange gestirà il redirect
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  };

  const signOut = async () => {
    try {
      await supabase.auth.signOut();
    } catch (e) {
      console.error('signOut error:', e);
    } finally {
      setUser(null);
      setProfile(null);
      setLoading(false);
    }
  };

  return (
    <AuthContext.Provider value={{ user, profile, loading, isSuperAdmin, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuthContext(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuthContext must be used inside AuthProvider');
  return ctx;
}
